import { useEffect, useMemo, useState } from 'react';
import { FlexColumn, FormControls } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddTestForm, ColumnResource } from 'shared/types';
import { fontWeight } from '@elementary/common-ui/style/utils';
import ChipInput from '../ChipInput';
import SeveritySelector from '../SeveritySelector';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  columnsResourcesData: ColumnResource[];
};

const ColumnsRelationship = ({ methods, columnsResourcesData }: Props) => {
  const { control, watch, setValue } = methods;
  const [expanded, setExpanded] = useState(true);

  const resources = watch('resources');
  const columnName = watch('columnName');
  const tableName = watch('tableName');

  const target = useMemo(() => {
    if (resources.length > 1) {
      return null;
    }
    const resource = resources[0];
    return columnsResourcesData.find((col) => col.id === resource);
  }, [columnsResourcesData, resources]);

  const descriptionText = useMemo(
    () =>
      target
        ? `Each existing ${target.name} column in the ${
            target.model_name
          } table should have a correlating ${
            columnName || '{column_name}'
          } in the ${tableName || '{table_name}'} table`
        : `Each existing column should have a correlating ${
            columnName || '{column_name}'
          } in the ${tableName || '{table_name}'} table`,
    [columnName, tableName, target],
  );

  useEffect(() => {
    setValue('description', descriptionText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionText]);

  return (
    <FormControls.TestAccordion
      summary="Relationship"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: fontWeight('semibold') }}>
            Test configuration
          </Typography>
          <FormControls.Row>
            {target ? (
              <FormControls.FormText>
                Each existing <b>{target.name}</b> column in the{' '}
                <b>{target.model_name}</b> table should have a correlating
              </FormControls.FormText>
            ) : (
              <FormControls.FormText>
                Each existing column should have a correlating
              </FormControls.FormText>
            )}
            <Controller
              name="columnName"
              control={control}
              render={({ field }) => (
                <ChipInput
                  {...field}
                  textFieldSx={{ maxWidth: '120px' }}
                  placeholder="column name"
                />
              )}
            />
            <FormControls.FormText>in the</FormControls.FormText>
            <Controller
              name="tableName"
              control={control}
              render={({ field }) => (
                <ChipInput
                  {...field}
                  textFieldSx={{ maxWidth: '120px' }}
                  placeholder="table name"
                />
              )}
            />
            <FormControls.FormText>table</FormControls.FormText>
          </FormControls.Row>
          <Controller
            name="severity"
            control={control}
            defaultValue={{
              conditionalError: { operator: '!=', value: 0 },
            }}
            render={({ field }) => (
              <SeveritySelector {...field} allowConditional />
            )}
          />
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <TableTestDescriptionField
          defaultValue={descriptionText}
          methods={methods}
        />

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default ColumnsRelationship;
